// src/pages/OrdersArchivePage.js

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ColumnsConfigModal from "../components/ColumnsConfigModal";
import PdfOptionsModal from "../components/PdfOptionsModal"; // ← OK
import { API_URL } from "../config"; // ✅ Krok 1: jedno źródło API (z /api na końcu)

import {
  konftabelazlecenia,
  oczekujace,
  przydzielone,
  realizowanestatyczne,
  wstrzymane,
  walidacjastatyczne,
  zatwierdzone,
  zamkniete,
} from "../assets";

import "./OrdersPage.css"; // Zakładam, że używa tych samych stylów co OrdersPage

// Domyślna konfiguracja widoczności kolumn
const defaultConfig = {
  nr_ref: true,
  tytul: true,
  osoba_wprowadzajaca_id: true,
  osoba_wykonujaca_id: true,
  rodzaj_zgloszenia: true,
  obszar_id: true,
  klient_id: true,
  osoba_zlecajaca: true,
  data_zlecenia: true,
  status: true,
  data_przyjecia: true,
  data_zakonczenia: true,
  data_zamkniecia: true,
  osoba_walidujaca_id: true,
  osoba_zamykajaca_id: true,
  waga: true,
};

// Funkcja formatowania daty w formacie DD-MM-YYYY
function formatDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

// Funkcja zwracająca GIF dla statusu
function getStatusGif(status) {
  switch (status) {
    case "Oczekujące":
      return oczekujace;
    case "Przydzielone":
      return przydzielone;
    case "Realizowane":
      return realizowanestatyczne;
    case "Wstrzymane":
      return wstrzymane;
    case "Walidacja":
      return walidacjastatyczne;
    case "Zatwierdzone":
      return zatwierdzone;
    case "Zamknięte":
      return zamkniete;
    default:
      return null;
  }
}

function OrdersArchivePage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [columnsConfig, setColumnsConfig] = useState(() => {
    const saved = localStorage.getItem("ordersArchiveColumnsConfig");
    return saved ? JSON.parse(saved) : defaultConfig;
  });
  const [showColumnsModal, setShowColumnsModal] = useState(false);

  // ** STAN I HANDLERY DLA RAPORTU ZBIOROWEGO **
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showBatchModal, setShowBatchModal] = useState(false);

  useEffect(() => {
    fetchArchiveOrders();
  }, []);

  const fetchArchiveOrders = async () => {
    try {
      const response = await fetch(`${API_URL}/orders?archiwum=true`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Błąd podczas pobierania zleceń archiwalnych");
      }
      const data = await response.json();
      setOrders(Array.isArray(data) ? data : []);
      setSelectedOrders([]);
    } catch (error) {
      console.error(error);
    }
  };

  // ** FUNKCJA GENERUJĄCA ZBIOROWY PDF **
  async function handleGenerateBatch({ includeWorkTime, includeMaterials, includePrice }) {
    const params = new URLSearchParams({
      ids: selectedOrders.join(","),
      includeWorkTime: String(includeWorkTime),
      includeMaterials: String(includeMaterials),
      includePrice: String(includePrice),
    }).toString();

    try {
      // ✅ Krok 2: bez Render, bez /api (bo API_URL już ma /api)
      const res = await fetch(`${API_URL}/orders/batch/pdf?${params}`, {
        credentials: "include",
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "Raport_Zbiorowy.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Błąd pobierania PDF-a:", err);
      alert("Nie udało się pobrać raportu. Sprawdź konsolę.");
    } finally {
      setShowBatchModal(false);
    }
  }

  // Filtrowanie zleceń
  const filteredOrders = orders.filter(
    (order) =>
      order.tytul.toLowerCase().includes(searchText.toLowerCase()) ||
      order.nr_ref.toLowerCase().includes(searchText.toLowerCase())
  );

  // Sortowanie zleceń
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    if (!sortField) return 0;
    let valA = a[sortField];
    let valB = b[sortField];
    if (typeof valA === "string" && valA) {
      valA = valA.toLowerCase();
      valB = valB.toLowerCase();
    }
    if (valA < valB) return sortOrder === "asc" ? -1 : 1;
    if (valA > valB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  // Handlery
  const handleSort = (field) => {
    let newOrder = "asc";
    if (sortField === field && sortOrder === "asc") newOrder = "desc";
    setSortField(field);
    setSortOrder(newOrder);
  };

  const handleRowClick = (id) => {
    navigate(`/zlecenia/${id}`);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleSavePermanent = (config) => {
    setColumnsConfig(config);
    localStorage.setItem("ordersArchiveColumnsConfig", JSON.stringify(config));
    setShowColumnsModal(false);
  };

  const handleSaveTemporary = (config) => {
    setColumnsConfig(config);
    setShowColumnsModal(false);
  };

  const handleRestoreDefaults = () => {
    setColumnsConfig(defaultConfig);
    localStorage.setItem("ordersArchiveColumnsConfig", JSON.stringify(defaultConfig));
    setShowColumnsModal(false);
  };

  return (
    <div className="orders-archive-page">
      <header
        className="orders-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2>Archiwum Zleceń</h2>
        <div
          className="orders-header-buttons"
          style={{ display: "flex", gap: "1rem", alignItems: "center" }}
        >
          <button className="btn-submit" onClick={fetchArchiveOrders}>
            Odśwież
          </button>

          {/* PRZYCISK RAPORT ZBIOROWY */}
          <button
            className="btn-submit"
            disabled={selectedOrders.length === 0}
            onClick={() => setShowBatchModal(true)}
          >
            Raport Zbiorowy
          </button>

          <button
            className="btn-config"
            onClick={() => setShowColumnsModal(true)}
            style={{ background: "transparent", border: "none", cursor: "pointer" }}
          >
            <img
              src={konftabelazlecenia}
              alt="Konfiguracja tabeli"
              style={{ width: "24px", height: "24px" }}
            />
          </button>
        </div>
      </header>

      <section className="orders-controls" style={{ margin: "1rem 0" }}>
        <input
          type="text"
          placeholder="Wyszukaj zlecenie..."
          value={searchText}
          onChange={handleSearchChange}
          style={{ padding: "0.5rem", width: "250px", marginRight: "1rem" }}
        />
        <button className="btn-submit" onClick={fetchArchiveOrders}>
          Szukaj
        </button>
      </section>

      <section className="orders-table-container">
        <table className="orders-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {/* CHECKBOX WSZYSTKIE */}
              <th>
                <input
                  type="checkbox"
                  checked={
                    selectedOrders.length === sortedOrders.length &&
                    sortedOrders.length > 0
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedOrders(sortedOrders.map((o) => o.id));
                    } else {
                      setSelectedOrders([]);
                    }
                  }}
                />
              </th>

              {columnsConfig.nr_ref && (
                <th onClick={() => handleSort("nr_ref")} style={{ cursor: "pointer", padding: "8px" }}>
                  Nr.ref
                </th>
              )}
              {columnsConfig.tytul && (
                <th onClick={() => handleSort("tytul")} style={{ cursor: "pointer", padding: "8px" }}>
                  Tytuł
                </th>
              )}
              {columnsConfig.waga && (
                <th onClick={() => handleSort("waga")} style={{ cursor: "pointer", padding: "8px" }}>
                  Priorytet
                </th>
              )}
              {columnsConfig.rodzaj_zgloszenia && (
                <th onClick={() => handleSort("rodzaj_zgloszenia")} style={{ cursor: "pointer", padding: "8px" }}>
                  Rodzaj
                </th>
              )}
              {columnsConfig.osoba_wprowadzajaca_id && (
                <th onClick={() => handleSort("osoba_wprowadzajaca_id")} style={{ cursor: "pointer", padding: "8px" }}>
                  Zgłaszający
                </th>
              )}
              {columnsConfig.osoba_wykonujaca_id && (
                <th onClick={() => handleSort("osoba_wykonujaca_id")} style={{ cursor: "pointer", padding: "8px" }}>
                  Przydzielony
                </th>
              )}
              {columnsConfig.status && (
                <th onClick={() => handleSort("status")} style={{ cursor: "pointer", padding: "8px", textAlign: "center" }}>
                  Status
                </th>
              )}
              {columnsConfig.data_zlecenia && (
                <th onClick={() => handleSort("data_zlecenia")} style={{ cursor: "pointer", padding: "8px" }}>
                  Data Zgłoszenia
                </th>
              )}
              {columnsConfig.data_zakonczenia && (
                <th onClick={() => handleSort("data_zakonczenia")} style={{ cursor: "pointer", padding: "8px" }}>
                  DeadLine
                </th>
              )}
              {columnsConfig.data_zamkniecia && (
                <th onClick={() => handleSort("data_zamkniecia")} style={{ cursor: "pointer", padding: "8px" }}>
                  Data Zamknięcia
                </th>
              )}
              {columnsConfig.data_przyjecia && (
                <th onClick={() => handleSort("data_przyjecia")} style={{ cursor: "pointer", padding: "8px" }}>
                  Data Przyjęcia
                </th>
              )}
              {columnsConfig.obszar_id && (
                <th onClick={() => handleSort("obszar_id")} style={{ cursor: "pointer", padding: "8px" }}>
                  Obszar
                </th>
              )}
              {columnsConfig.klient_id && (
                <th onClick={() => handleSort("klient_id")} style={{ cursor: "pointer", padding: "8px" }}>
                  Klient
                </th>
              )}
              {columnsConfig.osoba_zlecajaca && (
                <th onClick={() => handleSort("osoba_zlecajaca")} style={{ cursor: "pointer", padding: "8px" }}>
                  Zlecający
                </th>
              )}
              {columnsConfig.osoba_walidujaca_id && (
                <th onClick={() => handleSort("osoba_walidujaca_id")} style={{ cursor: "pointer", padding: "8px" }}>
                  Walidujący
                </th>
              )}
              {columnsConfig.osoba_zamykajaca_id && (
                <th onClick={() => handleSort("osoba_zamykajaca_id")} style={{ cursor: "pointer", padding: "8px" }}>
                  Zamykający
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {sortedOrders.map((order) => (
              <tr
                key={order.id}
                onClick={() => handleRowClick(order.id)}
                style={{ borderBottom: "1px solid #ccc", cursor: "pointer" }}
              >
                {/* CHECKBOX POJEDYNCZE */}
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order.id)}
                    onChange={(e) => {
                      const id = order.id;
                      setSelectedOrders((sel) =>
                        e.target.checked ? [...sel, id] : sel.filter((x) => x !== id)
                      );
                    }}
                  />
                </td>

                {columnsConfig.nr_ref && <td style={{ padding: "8px" }}>{order.nr_ref}</td>}
                {columnsConfig.tytul && <td style={{ padding: "8px" }}>{order.tytul}</td>}
                {columnsConfig.waga && <td style={{ padding: "8px" }}>{order.waga}</td>}
                {columnsConfig.rodzaj_zgloszenia && <td style={{ padding: "8px" }}>{order.rodzaj_zgloszenia}</td>}

                {columnsConfig.osoba_wprowadzajaca_id && (
                  <td style={{ padding: "8px" }}>
                    {order.first_name && order.last_name ? `${order.first_name} ${order.last_name}` : "-"}
                  </td>
                )}

                {columnsConfig.osoba_wykonujaca_id && (
                  <td style={{ padding: "8px" }}>
                    {order.executor_first_name && order.executor_last_name
                      ? `${order.executor_first_name} ${order.executor_last_name}`
                      : "-"}
                  </td>
                )}

                {columnsConfig.status && (
                  <td style={{ padding: "8px", textAlign: "center" }}>
                    {getStatusGif(order.status) ? (
                      <img
                        src={getStatusGif(order.status)}
                        alt={order.status}
                        className="status-gif"
                      />
                    ) : (
                      order.status
                    )}
                  </td>
                )}

                {columnsConfig.data_zlecenia && (
                  <td style={{ padding: "8px" }}>{formatDate(order.data_zlecenia)}</td>
                )}
                {columnsConfig.data_zakonczenia && (
                  <td style={{ padding: "8px" }}>{formatDate(order.deadline)}</td>
                )}
                {columnsConfig.data_zamkniecia && (
                  <td style={{ padding: "8px" }}>{formatDate(order.data_zamkniecia)}</td>
                )}
                {columnsConfig.data_przyjecia && (
                  <td style={{ padding: "8px" }}>{formatDate(order.data_przyjecia)}</td>
                )}
                {columnsConfig.obszar_id && (
                  <td style={{ padding: "8px" }}>{order.area_name ? order.area_name : "-"}</td>
                )}
                {columnsConfig.klient_id && (
                  <td style={{ padding: "8px" }}>{order.client_name ? order.client_name : "-"}</td>
                )}
                {columnsConfig.osoba_zlecajaca && (
                  <td style={{ padding: "8px" }}>{order.osoba_zlecajaca ? order.osoba_zlecajaca : "-"}</td>
                )}
                {columnsConfig.osoba_walidujaca_id && (
                  <td style={{ padding: "8px" }}>
                    {order.walidujaca_first_name && order.walidujaca_last_name
                      ? `${order.walidujaca_first_name} ${order.walidujaca_last_name}`
                      : "-"}
                  </td>
                )}
                {columnsConfig.osoba_zamykajaca_id && (
                  <td style={{ padding: "8px" }}>
                    {order.zamykajaca_first_name && order.zamykajaca_last_name
                      ? `${order.zamykajaca_first_name} ${order.zamykajaca_last_name}`
                      : "-"}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {showColumnsModal && (
        <ColumnsConfigModal
          initialConfig={columnsConfig}
          onSavePermanent={handleSavePermanent}
          onSaveTemporary={handleSaveTemporary}
          onRestoreDefaults={handleRestoreDefaults}
          onCancel={() => setShowColumnsModal(false)}
        />
      )}

      {/* MODAL OPCJI PDF ZBIOROWEGO */}
      {showBatchModal && (
        <PdfOptionsModal
          visible
          onClose={() => setShowBatchModal(false)}
          onGenerate={handleGenerateBatch}
        />
      )}
    </div>
  );
}

export default OrdersArchivePage;
